import * as React from "react";
import { Form, Formik, useFormikContext } from "formik";
import styled, { css } from "styled-components";
import { Icon, MultiSelect } from "components/common";
import SearchButton from "views/components/searchbar/SearchButton";
import TimeRangeFilter from "views/components/searchbar/time-range-filter";
import TimeRangeInputSettingsContext from "views/components/contexts/TimeRangeInputSettingsContext";
import { isTimeRange } from "views/typeGuards/timeRange";
import useUsageFilterOptions from "ai-management/token-usage/hooks/useUsageFilterOptions";
const CONTROL_HEIGHT = "34px";
const timeRangeInputSettings = {
  showDropdownButton: false,
  showPresetsButton: true,
  showAddToQuickListButton: false
};
const SearchRow = styled.div(
  ({ theme }) => css`
    display: grid;
    grid-template-columns: auto minmax(0, 2fr) minmax(0, 3fr);
    gap: ${theme.spacings.sm};
    align-items: start;

    @media (max-width: ${theme.breakpoints.max.md}) {
      grid-template-columns: auto minmax(0, 1fr);
    }
  `
);
const Filters = styled.div(
  ({ theme }) => css`
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: ${theme.spacings.sm};

    @media (max-width: ${theme.breakpoints.max.md}) {
      grid-column: 1 / -1;
    }

    @media (max-width: ${theme.breakpoints.max.sm}) {
      grid-template-columns: minmax(0, 1fr);
    }
  `
);
const StyledTimeRangeFilter = styled(TimeRangeFilter)`
  min-width: 0;
  height: ${CONTROL_HEIGHT};
`;
const NO_USAGE_HINT = "No usage in the selected time range";
const NoUsageIcon = styled(Icon)(
  ({ theme }) => css`
    margin-right: ${theme.spacings.xxs};
  `
);
const NoUsageLabel = styled.span`
  font-style: italic;
`;
const renderOption = ({ label, hasUsage }) => hasUsage ? /* @__PURE__ */ React.createElement("span", null, label) : /* @__PURE__ */ React.createElement(NoUsageLabel, { title: NO_USAGE_HINT }, /* @__PURE__ */ React.createElement(NoUsageIcon, { name: "info", size: "xs", "aria-label": NO_USAGE_HINT }), label);
const toSelectValue = (values) => values.join(",");
const fromSelectValue = (value) => value.split(",").filter(Boolean);
const FilterSelect = ({ name, label, options, isLoading }) => {
  const { values, setFieldValue } = useFormikContext();
  return /* @__PURE__ */ React.createElement(
    MultiSelect,
    {
      id: `token-usage-${name}`,
      inputId: `token-usage-${name}`,
      "aria-label": label,
      placeholder: isLoading ? `All ${label.toLowerCase()}` : `All ${label.toLowerCase()} (${options.length})`,
      options,
      value: toSelectValue(values[name]),
      onChange: (value) => setFieldValue(name, fromSelectValue(value)),
      valueRenderer: renderOption,
      optionRenderer: renderOption,
      clearable: false,
      isLoading
    }
  );
};
const UsageSearchFormFields = ({ isSearching }) => {
  const { values, setFieldValue, dirty } = useFormikContext();
  const { data: options, isLoading } = useUsageFilterOptions(values);
  return /* @__PURE__ */ React.createElement(SearchRow, null, /* @__PURE__ */ React.createElement(SearchButton, { dirty, displaySpinner: isSearching }), /* @__PURE__ */ React.createElement(TimeRangeInputSettingsContext.Provider, { value: timeRangeInputSettings }, /* @__PURE__ */ React.createElement(
    StyledTimeRangeFilter,
    {
      value: values.timerange,
      onChange: (timerange) => {
        if (isTimeRange(timerange)) {
          setFieldValue("timerange", timerange);
        }
      },
      limitDuration: 0,
      position: "bottom-start",
      submitOnPresetChange: false
    }
  )), /* @__PURE__ */ React.createElement(Filters, null, /* @__PURE__ */ React.createElement(FilterSelect, { name: "feature_id", label: "Features", options: options.features, isLoading }), /* @__PURE__ */ React.createElement(FilterSelect, { name: "provider", label: "Providers", options: options.providers, isLoading }), /* @__PURE__ */ React.createElement(FilterSelect, { name: "model", label: "Models", options: options.models, isLoading })));
};
const UsageSearchForm = ({ search, onSearch, isSearching }) => /* @__PURE__ */ React.createElement(Formik, { initialValues: search, enableReinitialize: true, onSubmit: onSearch }, /* @__PURE__ */ React.createElement(Form, null, /* @__PURE__ */ React.createElement(UsageSearchFormFields, { isSearching })));
export default UsageSearchForm;
